import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, fmtErr, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHead } from "@/components/desk/DeskLayout";
import { StatusChip, ROLE_LABELS } from "@/components/desk/StatusChip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const ROLES = Object.keys(ROLE_LABELS);

const RolePicker = ({ value, onChange }) => (
  <div className="grid grid-cols-2 gap-2" data-testid="role-picker">
    {ROLES.map((r) => (
      <label key={r} className="flex items-center gap-2 text-sm">
        <input type="checkbox" data-testid={`role-${r}`} checked={value.includes(r)}
          onChange={() => onChange(value.includes(r) ? value.filter((x) => x !== r) : [...value, r])} />
        {ROLE_LABELS[r]}
      </label>
    ))}
  </div>
);

export default function UsersPage() {
  const { user: me } = useAuth();
  const [rows, setRows] = useState([]);
  const [role, setRole] = useState("");
  const [edit, setEdit] = useState(null);
  const [create, setCreate] = useState(null);

  const load = () => api.get("/admin/users", { params: { role: role || undefined } }).then((r) => setRows(r.data));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  const patch = async (id, body) => {
    try {
      await api.patch(`/admin/users/${id}`, body);
      toast.success("User updated");
      setEdit(null);
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  const add = async () => {
    try {
      await api.post("/admin/users", create);
      toast.success("Staff account created");
      setCreate(null);
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  return (
    <div data-testid="users-page">
      <PageHead title="Users & roles" sub="Assign one or more roles. Approval roles can be combined.">
        <select data-testid="users-role-filter" className="h-9 border border-zinc-300 bg-white px-2 text-sm rounded-sm" value={role} onChange={(e) => setRole(e.target.value)}>
          <option value="">All roles</option>
          {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
        </select>
        <Button className="rounded-sm" data-testid="users-add-btn" onClick={() => setCreate({ name: "", email: "", password: "", roles: ["editor"] })}>Add staff</Button>
      </PageHead>
      <div className="bg-white border border-zinc-200 overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Roles</TableHead><TableHead>Status</TableHead><TableHead>Joined</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {rows.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                <TableCell><p className="font-semibold">{u.name}</p><p className="text-xs text-zinc-500">{u.email}</p></TableCell>
                <TableCell className="text-sm">{u.roles.map((r) => ROLE_LABELS[r]).join(", ")}</TableCell>
                <TableCell><StatusChip status={u.status} /></TableCell>
                <TableCell className="text-xs text-zinc-500 font-mono-meta">{fmtDate(u.created_at)}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="sm" variant="outline" className="rounded-sm mr-2" data-testid={`user-edit-${u.id}`} onClick={() => setEdit({ ...u })}>Roles</Button>
                  {u.id !== me.id && ["active", "disabled"].includes(u.status) && (
                    <Button size="sm" variant="ghost" className="rounded-sm" data-testid={`user-toggle-${u.id}`}
                      onClick={() => patch(u.id, { status: u.status === "active" ? "disabled" : "active" })}>
                      {u.status === "active" ? "Disable" : "Enable"}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="bg-white" data-testid="user-roles-dialog">
          <DialogHeader><DialogTitle>Roles for {edit?.name}</DialogTitle></DialogHeader>
          {edit && <RolePicker value={edit.roles} onChange={(roles) => setEdit({ ...edit, roles })} />}
          <DialogFooter><Button data-testid="user-roles-save" onClick={() => patch(edit.id, { roles: edit.roles })}>Save roles</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!create} onOpenChange={(o) => !o && setCreate(null)}>
        <DialogContent className="bg-white" data-testid="user-create-dialog">
          <DialogHeader><DialogTitle>Add staff account</DialogTitle></DialogHeader>
          {create && (
            <div className="space-y-3">
              <Input data-testid="user-create-name" placeholder="Full name" value={create.name} onChange={(e) => setCreate({ ...create, name: e.target.value })} />
              <Input data-testid="user-create-email" placeholder="Email" type="email" value={create.email} onChange={(e) => setCreate({ ...create, email: e.target.value })} />
              <Input data-testid="user-create-password" placeholder="Temporary password (8+ chars, letters and numbers)" type="password" value={create.password} onChange={(e) => setCreate({ ...create, password: e.target.value })} />
              <RolePicker value={create.roles} onChange={(roles) => setCreate({ ...create, roles })} />
            </div>
          )}
          <DialogFooter><Button data-testid="user-create-submit" onClick={add}>Create account</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
